import React, { useState, useEffect } from 'react';
import ApiClient from "../utils/ApiClient";

export default function XeroDocuments() {
    const [documents, setDocuments] = useState([]);
    const [documentType, setDocumentType] = useState('invoices');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [syncingState, setSyncingState] = useState({}); // Track syncing state per button
    const itemsPerPage = 100;

    // Manual link modal state
    const [manualLinkModal, setManualLinkModal] = useState(null); // { doc, syncType, accountName, docTypeToSearch }
    const [xeroDocuments, setXeroDocuments] = useState([]);
    const [xeroDocSearchTerm, setXeroDocSearchTerm] = useState('');
    const [loadingXeroDocs, setLoadingXeroDocs] = useState(false);
    const [xeroDocsError, setXeroDocsError] = useState(null);
    const [connectionErrors, setConnectionErrors] = useState([]); // Xero orgs that refused access (need reconnect)
    const [referenceSearch, setReferenceSearch] = useState('');
    const [referenceSearching, setReferenceSearching] = useState(false);
    const [foundDraftBill, setFoundDraftBill] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Payment status state
    const [paymentStatuses, setPaymentStatuses] = useState({}); // {documentNumber: {doradoBillStatus: 'PAID', rhinoBillStatus: 'AUTHORISED'}}
    const [checkingPayments, setCheckingPayments] = useState(false);

    // Add invoice modal state
    const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
    const [newInvoice, setNewInvoice] = useState({
        invoiceNumber: '',
        poNumber: '',
        invoiceDate: '',
        amount: '',
        supplierId: ''
    });
    const [invoicePdfFile, setInvoicePdfFile] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [savingInvoice, setSavingInvoice] = useState(false);
    const [addInvoiceError, setAddInvoiceError] = useState(null);

    // Edit invoice modal state
    const [editInvoiceModal, setEditInvoiceModal] = useState(null); // holds invoice being edited
    const [editingInvoice, setEditingInvoice] = useState(false);
    const [editInvoiceError, setEditInvoiceError] = useState(null);

    // Delete confirmation state
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(null); // holds invoice to delete
    const [deletingInvoice, setDeletingInvoice] = useState(false);

    useEffect(() => {
        fetchDocuments();
    }, [documentType]);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await ApiClient.get(`/api/purchasing/xero-documents?type=${documentType}`);
            setDocuments(response.data);
            setCurrentPage(1);

            // Load cached payment statuses for synced documents
            const syncedDocNumbers = response.data
                .filter(doc => doc.doradoBillSynced || doc.rhinoBillSynced || doc.doradoInvoiceSynced)
                .map(doc => doc.documentNumber);

            if (syncedDocNumbers.length > 0) {
                try {
                    const statusResponse = await ApiClient.post('/api/purchasing/xero/cached-payment-status', syncedDocNumbers);
                    if (statusResponse.data.success) {
                        setPaymentStatuses(statusResponse.data.statuses || {});
                    }
                } catch (statusError) {
                    console.error('Error loading cached payment statuses:', statusError);
                }
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    const formatDate = (value) => {
        if (!value) return '-';
        // Append T00:00:00 to parse as local time, not UTC
        const date = value.includes('T') ? new Date(value) : new Date(value + 'T00:00:00');
        return date.toLocaleDateString();
    };

    // Pagination logic
    const totalPages = Math.ceil(documents.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentDocuments = documents.slice(startIndex, endIndex);

    const handlePrevPage = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
    };

    // Open manual link modal
    const handleOpenManualLink = async (doc, syncType) => {
        // Determine which Xero account and doc type to search
        const accountName = syncType.startsWith('dorado') ? 'dorado' : 'rhino';
        const docTypeToSearch = syncType.includes('bill') ? 'bill' : 'invoice';

        setManualLinkModal({ doc, syncType, accountName, docTypeToSearch });
        setXeroDocSearchTerm('');
        setXeroDocuments([]);
        setReferenceSearch('');
        setFoundDraftBill(null);
        setXeroDocsError(null);
        setLoadingXeroDocs(true);

        try {
            const response = await ApiClient.get(`/api/xero/documents/search`, {
                params: { accountName, docType: docTypeToSearch }
            });
            setXeroDocuments(response.data);
        } catch (error) {
            console.error('Error fetching Xero documents:', error);
            setXeroDocsError(error.response?.data?.error || error.message);
        } finally {
            setLoadingXeroDocs(false);
        }
    };

    // Find draft bill by Xero invoice number
    const handleFindByXeroNumber = async () => {
        if (!manualLinkModal || !referenceSearch.trim()) return;

        setReferenceSearching(true);
        setFoundDraftBill(null);

        try {
            const response = await ApiClient.get('/api/xero/documents/draft-bill', {
                params: {
                    accountName: manualLinkModal.accountName,
                    invoiceNumber: referenceSearch.trim()
                }
            });
            setFoundDraftBill(response.data);
        } catch (error) {
            if (error.response?.status === 404) {
                alert('No draft bill found with Xero #: ' + referenceSearch);
            } else {
                console.error('Error finding draft bill:', error);
                alert('Error: ' + (error.response?.data?.error || error.message));
            }
        } finally {
            setReferenceSearching(false);
        }
    };

    // Select and link a Xero document
    const handleSelectXeroDoc = async (xeroDoc) => {
        if (!manualLinkModal) return;

        const { doc, syncType, accountName } = manualLinkModal;

        try {
            await ApiClient.post('/api/xero/documents/link', {
                sourceDocNumber: doc.documentNumber,
                sourceDocType: documentType === 'invoices' ? 'invoice' : 'credit',
                xeroAccount: accountName,
                syncType: syncType,
                xeroInvoiceId: xeroDoc.invoiceId,
                xeroInvoiceNumber: xeroDoc.invoiceNumber,
                amount: doc.amount,
                description: `Supplier ${documentType === 'invoices' ? 'Invoice' : 'Credit'} #${doc.documentNumber}`
            });

            // Show success message
            const accountLabel = accountName === 'dorado' ? 'Dorado' : 'Rhino';
            setSuccessMessage(`Successfully linked ${doc.documentNumber} to Xero ${xeroDoc.invoiceNumber} (${accountLabel})`);

            // Auto-hide success message after 5 seconds
            setTimeout(() => setSuccessMessage(null), 5000);

            // Refresh documents and close modal
            await fetchDocuments();
            setManualLinkModal(null);
        } catch (error) {
            console.error('Error linking document:', error);
            alert('Error linking document: ' + (error.response?.data?.error || error.message));
        }
    };

    // Filter Xero documents by search term
    const filteredXeroDocs = xeroDocuments.filter(doc => {
        if (!xeroDocSearchTerm) return true;
        const term = xeroDocSearchTerm.toLowerCase();
        return (
            (doc.invoiceNumber && doc.invoiceNumber.toLowerCase().includes(term)) ||
            (doc.contact && doc.contact.toLowerCase().includes(term)) ||
            (doc.reference && doc.reference.toLowerCase().includes(term))
        );
    });

    // Sync to specific Xero account
    const handleSync = async (doc, syncType) => {
        const key = `${doc.documentNumber}-${syncType}`;
        setSyncingState(prev => ({ ...prev, [key]: 'syncing' }));

        try {
            const endpoint = `/api/purchasing/xero/${syncType}`;
            const response = await ApiClient.post(endpoint, {
                documentType: documentType === 'invoices' ? 'invoice' : 'credit',
                documentNumber: doc.documentNumber,
                amount: doc.amount,
                poNumber: doc.poNumber,
                date: doc.date
            });

            if (response.data.success) {
                // Refresh documents to get updated sync status
                await fetchDocuments();
                setSyncingState(prev => ({ ...prev, [key]: 'success' }));
            } else {
                setSyncingState(prev => ({ ...prev, [key]: 'error', [`${key}-message`]: response.data.error }));
            }
        } catch (error) {
            console.error('Sync error:', error);
            setSyncingState(prev => ({
                ...prev,
                [key]: 'error',
                [`${key}-message`]: error.response?.data?.error || error.message
            }));
        }
    };

    // Unlink a document from Xero (void and remove sync record)
    const handleUnlink = async (doc, syncType) => {
        if (!window.confirm(`Are you sure you want to unlink and void this document in Xero? This cannot be undone.`)) {
            return;
        }

        const key = `${doc.documentNumber}-${syncType}`;
        setSyncingState(prev => ({ ...prev, [key]: 'unlinking' }));

        try {
            const response = await ApiClient.delete(`/api/purchasing/xero/unlink?documentNumber=${doc.documentNumber}&syncType=${syncType}`);

            if (response.data.success) {
                // Refresh documents to get updated sync status
                await fetchDocuments();
                setSyncingState(prev => ({ ...prev, [key]: null }));
            } else {
                setSyncingState(prev => ({ ...prev, [key]: 'error', [`${key}-message`]: response.data.error }));
            }
        } catch (error) {
            console.error('Unlink error:', error);
            setSyncingState(prev => ({
                ...prev,
                [key]: 'error',
                [`${key}-message`]: error.response?.data?.error || error.message
            }));
        }
    };

    // Check payment status for all synced documents
    const handleCheckPaymentStatus = async () => {
        // Get all synced document numbers (bills and invoices) that are NOT already PAID
        const syncedDocs = documents.filter(doc => {
            if (!doc.doradoBillSynced && !doc.rhinoBillSynced && !doc.doradoInvoiceSynced) {
                return false; // Not synced at all
            }
            // Check if any sync type is NOT paid yet
            const statuses = paymentStatuses[doc.documentNumber] || {};
            const doradoBillPaid = !doc.doradoBillSynced || statuses.doradoBillStatus === 'PAID';
            const rhinoBillPaid = !doc.rhinoBillSynced || statuses.rhinoBillStatus === 'PAID';
            const doradoInvoicePaid = !doc.doradoInvoiceSynced || statuses.doradoInvoiceStatus === 'PAID';

            // Include if ANY synced item is not yet paid
            return !doradoBillPaid || !rhinoBillPaid || !doradoInvoicePaid;
        }).map(doc => doc.documentNumber);

        console.log('Checking payment status for documents:', syncedDocs);

        if (syncedDocs.length === 0) {
            setSuccessMessage('All synced documents are already marked as PAID');
            setTimeout(() => setSuccessMessage(null), 3000);
            return;
        }

        setCheckingPayments(true);

        try {
            const response = await ApiClient.post('/api/purchasing/xero/check-payment-status', syncedDocs, { timeout: 120000 });
            console.log('Payment status response:', response.data);

            if (response.data.success) {
                const newStatuses = response.data.statuses || {};
                setPaymentStatuses(prev => ({...prev, ...newStatuses}));
                setConnectionErrors(response.data.connectionErrors || []);

                // Count how many changed to PAID
                let paidCount = 0;
                Object.entries(newStatuses).forEach(([docNum, statuses]) => {
                    if (statuses.doradoBillStatus === 'PAID') paidCount++;
                    if (statuses.rhinoBillStatus === 'PAID') paidCount++;
                    if (statuses.doradoInvoiceStatus === 'PAID') paidCount++;
                });

                setSuccessMessage(`Payment check complete. Checked ${syncedDocs.length} documents.`);
                setTimeout(() => setSuccessMessage(null), 3000);
            } else {
                console.error('Failed to check payment status:', response.data.error);
                setSuccessMessage('Error: ' + (response.data.error || 'Failed to check payment status'));
                setTimeout(() => setSuccessMessage(null), 5000);
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
            setSuccessMessage('Error checking payment status: ' + error.message);
            setTimeout(() => setSuccessMessage(null), 5000);
        } finally {
            setCheckingPayments(false);
        }
    };

    // Open add invoice modal and fetch suppliers
    const openAddInvoiceModal = async () => {
        setShowAddInvoiceModal(true);
        setNewInvoice({
            invoiceNumber: '',
            poNumber: '',
            invoiceDate: '',
            amount: '',
            supplierId: ''
        });
        setInvoicePdfFile(null);
        setAddInvoiceError(null);

        // Fetch suppliers if not already loaded
        if (suppliers.length === 0) {
            try {
                const res = await ApiClient.get('/api/xero/suppliers');
                setSuppliers(res.data);
            } catch (err) {
                console.error('Error fetching suppliers:', err);
            }
        }
    };

    // Handle file input change
    const handleInvoiceFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setInvoicePdfFile(file);
            setAddInvoiceError(null);
        } else if (file) {
            setAddInvoiceError('Please select a PDF file');
            e.target.value = '';
        }
    };

    // Save manual invoice
    const handleSaveInvoice = async () => {
        // Validate required fields
        if (!newInvoice.invoiceNumber || !newInvoice.invoiceDate ||
            !newInvoice.amount || !newInvoice.supplierId) {
            setAddInvoiceError('Please fill in all required fields');
            return;
        }

        setSavingInvoice(true);
        setAddInvoiceError(null);

        try {
            // Use FormData for multipart file upload
            const formData = new FormData();
            formData.append('invoiceNumber', newInvoice.invoiceNumber);
            formData.append('poNumber', newInvoice.poNumber || '');
            formData.append('invoiceDate', newInvoice.invoiceDate);
            formData.append('amount', newInvoice.amount);
            formData.append('supplierId', newInvoice.supplierId);
            if (invoicePdfFile) {
                formData.append('file', invoicePdfFile);
            }

            await ApiClient.post('/api/purchasing/manual-invoice', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setShowAddInvoiceModal(false);
            setSuccessMessage(`Invoice ${newInvoice.invoiceNumber} created successfully`);
            setTimeout(() => setSuccessMessage(null), 5000);
            fetchDocuments(); // Refresh list
        } catch (err) {
            console.error('Error saving invoice:', err);
            setAddInvoiceError(err.response?.data?.error || err.message);
        } finally {
            setSavingInvoice(false);
        }
    };

    // Open edit invoice modal
    const openEditInvoiceModal = async (doc) => {
        setEditInvoiceModal({
            invoiceNumber: doc.documentNumber,
            poNumber: doc.poNumber || '',
            invoiceDate: doc.date || '',
            amount: doc.amount || '',
            supplierId: doc.supplierId || ''
        });
        setEditInvoiceError(null);

        // Fetch suppliers if not already loaded
        if (suppliers.length === 0) {
            try {
                const res = await ApiClient.get('/api/xero/suppliers');
                setSuppliers(res.data);
            } catch (err) {
                console.error('Error fetching suppliers:', err);
            }
        }
    };

    // Save edited invoice
    const handleSaveEditInvoice = async () => {
        if (!editInvoiceModal.invoiceDate || !editInvoiceModal.amount || !editInvoiceModal.supplierId) {
            setEditInvoiceError('Please fill in all required fields');
            return;
        }

        setEditingInvoice(true);
        setEditInvoiceError(null);

        try {
            await ApiClient.put(`/api/purchasing/invoice/${editInvoiceModal.invoiceNumber}`, {
                poNumber: editInvoiceModal.poNumber || null,
                invoiceDate: editInvoiceModal.invoiceDate,
                amount: editInvoiceModal.amount,
                supplierId: editInvoiceModal.supplierId
            });

            setEditInvoiceModal(null);
            setSuccessMessage(`Invoice ${editInvoiceModal.invoiceNumber} updated successfully`);
            setTimeout(() => setSuccessMessage(null), 5000);
            fetchDocuments();
        } catch (err) {
            console.error('Error updating invoice:', err);
            setEditInvoiceError(err.response?.data?.error || err.message);
        } finally {
            setEditingInvoice(false);
        }
    };

    // Delete invoice
    const handleDeleteInvoice = async () => {
        if (!deleteConfirmModal) return;

        setDeletingInvoice(true);

        try {
            await ApiClient.delete(`/api/purchasing/invoice/${deleteConfirmModal.documentNumber}`);
            setDeleteConfirmModal(null);
            setSuccessMessage(`Invoice ${deleteConfirmModal.documentNumber} deleted successfully`);
            setTimeout(() => setSuccessMessage(null), 5000);
            fetchDocuments();
        } catch (err) {
            console.error('Error deleting invoice:', err);
            alert(err.response?.data?.error || err.message);
        } finally {
            setDeletingInvoice(false);
        }
    };

    // Check if invoice is synced (any sync type)
    const isInvoiceSynced = (doc) => {
        return doc.doradoBillSynced || doc.doradoInvoiceSynced || doc.rhinoBillSynced;
    };

    // Render sync button based on status
    const renderSyncButton = (doc, syncType, isSynced, errorMessage) => {
        const key = `${doc.documentNumber}-${syncType}`;
        const syncState = syncingState[key];
        const errorMsg = errorMessage || syncingState[`${key}-message`];

        if (syncState === 'syncing' || syncState === 'unlinking') {
            return (
                <div className="inline-flex items-center">
                    <svg className="animate-spin h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="ml-1 text-xs text-gray-500">{syncState === 'unlinking' ? 'Unlinking...' : ''}</span>
                </div>
            );
        }

        if (isSynced) {
            // Determine payment status key based on syncType
            const statusKey = syncType === 'dorado-bill' ? 'doradoBillStatus' :
                              syncType === 'rhino-bill' ? 'rhinoBillStatus' :
                              syncType === 'dorado-invoice' ? 'doradoInvoiceStatus' : null;
            const docStatuses = paymentStatuses[doc.documentNumber] || {};
            const paymentStatus = statusKey ? docStatuses[statusKey] : null;
            const isPaid = paymentStatus === 'PAID';

            return (
                <div className="inline-flex items-center gap-1">
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Synced
                    </span>
                    {isPaid && (
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                            PAID
                        </span>
                    )}
                    <button
                        onClick={() => handleUnlink(doc, syncType)}
                        className="inline-flex items-center rounded-md bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 hover:bg-red-200"
                        title="Unlink and void in Xero"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            );
        }

        if (errorMsg) {
            return (
                <div className="relative group">
                    <button
                        onClick={() => handleSync(doc, syncType)}
                        className="inline-flex items-center rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-200"
                    >
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Retry
                    </button>
                    <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 bottom-full left-1/2 transform -translate-x-1/2 mb-1 whitespace-nowrap max-w-xs truncate">
                        {errorMsg}
                    </div>
                </div>
            );
        }

        return (
            <button
                onClick={() => handleSync(doc, syncType)}
                className="inline-flex items-center rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
            >
                Create
            </button>
        );
    };

    // Render bill button - shows Unlinked + Link when not synced, Synced + unlink when synced
    const renderBillButton = (doc, syncType, isSynced) => {
        const key = `${doc.documentNumber}-${syncType}`;
        const syncState = syncingState[key];

        if (syncState === 'syncing' || syncState === 'unlinking') {
            return (
                <div className="inline-flex items-center">
                    <svg className="animate-spin h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="ml-1 text-xs text-gray-500">{syncState === 'unlinking' ? 'Unlinking...' : 'Linking...'}</span>
                </div>
            );
        }

        if (isSynced) {
            // Determine payment status key based on syncType
            const statusKey = syncType === 'dorado-bill' ? 'doradoBillStatus' :
                              syncType === 'rhino-bill' ? 'rhinoBillStatus' :
                              syncType === 'dorado-invoice' ? 'doradoInvoiceStatus' : null;
            const docStatuses = paymentStatuses[doc.documentNumber] || {};
            const paymentStatus = statusKey ? docStatuses[statusKey] : null;
            const isPaid = paymentStatus === 'PAID';

            return (
                <div className="inline-flex items-center gap-1">
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Synced
                    </span>
                    {isPaid && (
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                            PAID
                        </span>
                    )}
                    <button
                        onClick={() => handleUnlink(doc, syncType)}
                        className="inline-flex items-center rounded-md bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 hover:bg-red-200"
                        title="Unlink and void in Xero"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            );
        }

        // Not synced - show Unlinked badge + Link button
        return (
            <div className="inline-flex items-center gap-1">
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                    Unlinked
                </span>
                <button
                    onClick={() => handleOpenManualLink(doc, syncType)}
                    className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                >
                    Link
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-gray-600">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-red-600">Error loading data: {error}</div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            {/* Success Message Toast */}
            {successMessage && (
                <div className="fixed top-4 right-4 z-50 animate-fade-in">
                    <div className="rounded-md bg-green-50 p-4 shadow-lg border border-green-200">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-green-800">{successMessage}</p>
                            </div>
                            <div className="ml-auto pl-3">
                                <button
                                    onClick={() => setSuccessMessage(null)}
                                    className="inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100"
                                >
                                    <span className="sr-only">Dismiss</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="sm:flex sm:items-center sm:justify-between">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-semibold text-gray-900">Xero Documents</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Sync invoices and credits to Xero for Dorado Distribution and Retail Rhino
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:flex sm:items-center sm:space-x-4">
                    {/* Document Type Dropdown */}
                    <select
                        value={documentType}
                        onChange={(e) => setDocumentType(e.target.value)}
                        className="block w-40 rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    >
                        <option value="invoices">Invoices</option>
                        <option value="credits">Credits</option>
                    </select>

                    {/* Check Payment Status Button */}
                    <button
                        onClick={handleCheckPaymentStatus}
                        disabled={checkingPayments}
                        className="inline-flex items-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {checkingPayments ? (
                            <>
                                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Checking...
                            </>
                        ) : (
                            <>
                                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Check Payments
                            </>
                        )}
                    </button>

                    {/* Add Invoice Button */}
                    <button
                        onClick={openAddInvoiceModal}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                    >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Invoice
                    </button>

                    {/* Settings Link */}
                    <a
                        href="/xero-settings"
                        className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                    >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Settings
                    </a>

                    {/* Refresh Button */}
                    <button
                        onClick={fetchDocuments}
                        className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Xero connection errors (from Check Payments) */}
            {connectionErrors.length > 0 && (
                <div className="mt-4 rounded-md bg-red-50 p-4 border border-red-200">
                    <div className="flex justify-between gap-4">
                        <div className="text-sm text-red-800">
                            <p className="font-semibold">Xero connection needs attention - payment statuses below may be out of date</p>
                            <ul className="mt-1 list-disc pl-5">
                                {connectionErrors.map(msg => <li key={msg}>{msg}</li>)}
                            </ul>
                        </div>
                        <div className="flex items-start gap-2">
                            <a href="/xero-settings" className="whitespace-nowrap rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700">
                                Xero Settings
                            </a>
                            <button onClick={() => setConnectionErrors([])} className="text-red-500 hover:text-red-700 text-sm">Dismiss</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Invoices Table */}
            {documentType === 'invoices' && (
                <div className="mt-8 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">#</th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Invoice #</th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">PO</th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                                            <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Amount</th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Items</th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">First Recv</th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last Recv</th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                                <div className="text-xs">Dorado</div>
                                                <div className="text-xs text-gray-500">Bill</div>
                                            </th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                                <div className="text-xs">Dorado</div>
                                                <div className="text-xs text-gray-500">Invoice</div>
                                            </th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                                <div className="text-xs">Rhino</div>
                                                <div className="text-xs text-gray-500">Bill</div>
                                            </th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {currentDocuments.length === 0 ? (
                                            <tr>
                                                <td colSpan={12} className="py-8 text-center text-sm text-gray-500">
                                                    No invoices found
                                                </td>
                                            </tr>
                                        ) : (
                                            currentDocuments.map((doc, index) => (
                                                <tr key={doc.documentNumber} className="hover:bg-gray-50">
                                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500">
                                                        {startIndex + index + 1}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-indigo-600">
                                                        <span className="inline-flex items-center gap-1">
                                                            {doc.documentNumber}
                                                            <a
                                                                href={`/api/purchasing/pdf/${doc.documentNumber}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="View PDF"
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                                                                </svg>
                                                            </a>
                                                        </span>
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {doc.poNumber || '-'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {formatDate(doc.date)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 text-right">
                                                        {formatCurrency(doc.amount)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                                        {doc.itemCount || 0}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {formatDate(doc.firstReceivedDate)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {formatDate(doc.lastReceivedDate)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                        {renderBillButton(doc, 'dorado-bill', doc.doradoBillSynced)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                        {doc.doradoInvoiceSynced ? (
                                                            <div className="inline-flex items-center gap-1">
                                                                <span className="text-gray-600 text-xs">{doc.doradoInvoiceXeroNumber || 'Synced'}</span>
                                                                {paymentStatuses[doc.documentNumber]?.doradoInvoiceStatus === 'PAID' && (
                                                                    <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                                                                        PAID
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            renderSyncButton(doc, 'dorado-invoice', doc.doradoInvoiceSynced, doc.doradoInvoiceError)
                                                        )}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                        {renderBillButton(doc, 'rhino-bill', doc.rhinoBillSynced)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                        <div className="inline-flex items-center gap-2">
                                                            {/* Edit Button */}
                                                            <button
                                                                onClick={() => openEditInvoiceModal(doc)}
                                                                disabled={isInvoiceSynced(doc)}
                                                                title={isInvoiceSynced(doc) ? "Unlink from Xero first" : "Edit invoice"}
                                                                className={`p-1 rounded ${isInvoiceSynced(doc) ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}
                                                            >
                                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                </svg>
                                                            </button>
                                                            {/* Delete Button */}
                                                            <button
                                                                onClick={() => setDeleteConfirmModal(doc)}
                                                                disabled={isInvoiceSynced(doc)}
                                                                title={isInvoiceSynced(doc) ? "Unlink from Xero first" : "Delete invoice"}
                                                                className={`p-1 rounded ${isInvoiceSynced(doc) ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-800 hover:bg-red-50'}`}
                                                            >
                                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Credits Table */}
            {documentType === 'credits' && (
                <div className="mt-8 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">#</th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Credit #</th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">PO</th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                                            <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Amount</th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Items</th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                                <div className="text-xs">Dorado</div>
                                                <div className="text-xs text-gray-500">Credit (from Schluter)</div>
                                            </th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                                <div className="text-xs">Dorado</div>
                                                <div className="text-xs text-gray-500">Credit (to Rhino)</div>
                                            </th>
                                            <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                                <div className="text-xs">Rhino</div>
                                                <div className="text-xs text-gray-500">Credit (from Dorado)</div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {currentDocuments.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="py-8 text-center text-sm text-gray-500">
                                                    No credits found
                                                </td>
                                            </tr>
                                        ) : (
                                            currentDocuments.map((doc, index) => (
                                                <tr key={doc.documentNumber} className="hover:bg-gray-50">
                                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500">
                                                        {startIndex + index + 1}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-indigo-600">
                                                        <span className="inline-flex items-center gap-1">
                                                            {doc.documentNumber}
                                                            <a
                                                                href={`/api/purchasing/pdf/${doc.documentNumber}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="View PDF"
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                                                                </svg>
                                                            </a>
                                                        </span>
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {doc.poNumber || '-'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {formatDate(doc.date)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 text-right">
                                                        {formatCurrency(doc.amount)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                                        {doc.itemCount || 0}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                        {renderSyncButton(doc, 'dorado-bill', doc.doradoBillSynced, doc.doradoBillError)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                        {renderSyncButton(doc, 'dorado-invoice', doc.doradoInvoiceSynced, doc.doradoInvoiceError)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                        {renderSyncButton(doc, 'rhino-bill', doc.rhinoBillSynced, doc.rhinoBillError)}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Pagination */}
            {documents.length > 0 && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div className="flex flex-1 justify-between sm:hidden">
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                                <span className="font-medium">{Math.min(endIndex, documents.length)}</span> of{' '}
                                <span className="font-medium">{documents.length}</span> results
                            </p>
                        </div>
                        <div>
                            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                <button
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="sr-only">Previous</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                                currentPage === pageNum
                                                    ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="sr-only">Next</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Link Modal */}
            {manualLinkModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setManualLinkModal(null)}></div>
                        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                                        <h3 className="text-lg font-semibold leading-6 text-gray-900">
                                            Link to Existing Xero {manualLinkModal.docTypeToSearch === 'bill' ? 'Bill' : 'Invoice'}
                                        </h3>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {manualLinkModal.accountName === 'dorado' ? 'Dorado Distribution' : 'Retail Rhino'} - {manualLinkModal.doc.documentNumber}
                                        </p>

                                        {/* Find Draft Bill by Xero Number */}
                                        {manualLinkModal.docTypeToSearch === 'bill' && (
                                            <div className="mt-4 p-3 bg-blue-50 rounded-md">
                                                <label className="block text-sm font-medium text-blue-800 mb-2">
                                                    Find Draft Bill by Xero #
                                                </label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Xero invoice number (e.g., BILL-0001)..."
                                                        value={referenceSearch}
                                                        onChange={(e) => setReferenceSearch(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleFindByXeroNumber()}
                                                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                    />
                                                    <button
                                                        onClick={handleFindByXeroNumber}
                                                        disabled={referenceSearching || !referenceSearch.trim()}
                                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                                    >
                                                        {referenceSearching ? 'Searching...' : 'Find'}
                                                    </button>
                                                </div>
                                                {/* Found Draft Bill Result */}
                                                {foundDraftBill && (
                                                    <div className="mt-3 p-2 bg-white rounded border border-blue-200">
                                                        <div className="flex justify-between items-center">
                                                            <div>
                                                                <span className="font-medium text-blue-700">{foundDraftBill.invoiceNumber}</span>
                                                                <span className="text-gray-500 ml-2">({foundDraftBill.contact})</span>
                                                                <span className="text-gray-500 ml-2">{formatCurrency(foundDraftBill.total)}</span>
                                                                <span className="ml-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800">
                                                                    {foundDraftBill.status}
                                                                </span>
                                                            </div>
                                                            <button
                                                                onClick={() => handleSelectXeroDoc(foundDraftBill)}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                                            >
                                                                Link This
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Search Input */}
                                        <div className="mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Or search all documents:</label>
                                            <input
                                                type="text"
                                                placeholder="Search by invoice number, contact, or reference..."
                                                value={xeroDocSearchTerm}
                                                onChange={(e) => setXeroDocSearchTerm(e.target.value)}
                                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            />
                                        </div>

                                        {/* Documents List */}
                                        <div className="mt-4 max-h-96 overflow-y-auto border rounded-md">
                                            {loadingXeroDocs ? (
                                                <div className="p-4 text-center text-gray-500">
                                                    <svg className="animate-spin h-6 w-6 mx-auto text-indigo-600" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <p className="mt-2">Loading documents from Xero...</p>
                                                </div>
                                            ) : xeroDocsError ? (
                                                <div className="p-4 text-center text-sm text-red-700 bg-red-50">
                                                    <p>{xeroDocsError}</p>
                                                    <a href="/xero-settings" className="mt-2 inline-block font-semibold underline">
                                                        Go to Xero Settings
                                                    </a>
                                                </div>
                                            ) : filteredXeroDocs.length === 0 ? (
                                                <div className="p-4 text-center text-gray-500">
                                                    No documents found
                                                </div>
                                            ) : (
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50 sticky top-0">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Xero #</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {filteredXeroDocs.map((xeroDoc) => (
                                                            <tr
                                                                key={xeroDoc.invoiceId}
                                                                onClick={() => handleSelectXeroDoc(xeroDoc)}
                                                                className="cursor-pointer hover:bg-indigo-50"
                                                            >
                                                                <td className="px-3 py-2 text-sm font-medium text-indigo-600">{xeroDoc.invoiceNumber}</td>
                                                                <td className="px-3 py-2 text-sm text-gray-700">{xeroDoc.contact || '-'}</td>
                                                                <td className="px-3 py-2 text-sm text-gray-500">{xeroDoc.date ? formatDate(xeroDoc.date) : '-'}</td>
                                                                <td className="px-3 py-2 text-sm text-gray-900 text-right">{formatCurrency(xeroDoc.total)}</td>
                                                                <td className="px-3 py-2 text-sm">
                                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                                                        xeroDoc.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                                                        xeroDoc.status === 'AUTHORISED' ? 'bg-blue-100 text-blue-800' :
                                                                        xeroDoc.status === 'DRAFT' ? 'bg-gray-100 text-gray-800' :
                                                                        'bg-yellow-100 text-yellow-800'
                                                                    }`}>
                                                                        {xeroDoc.status}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <button
                                    type="button"
                                    onClick={() => setManualLinkModal(null)}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Invoice Modal */}
            {showAddInvoiceModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowAddInvoiceModal(false)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Add Manual Invoice</h3>

                            {/* Error Message */}
                            {addInvoiceError && (
                                <div className="mb-4 rounded-md bg-red-50 p-3">
                                    <p className="text-sm text-red-700">{addInvoiceError}</p>
                                </div>
                            )}

                            {/* Form Fields */}
                            <div className="space-y-4">
                                {/* Invoice Number */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Invoice # <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newInvoice.invoiceNumber}
                                        onChange={(e) => setNewInvoice({ ...newInvoice, invoiceNumber: e.target.value })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        placeholder="e.g., 25397415"
                                    />
                                </div>

                                {/* PO Number */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">PO #</label>
                                    <input
                                        type="text"
                                        value={newInvoice.poNumber}
                                        onChange={(e) => setNewInvoice({ ...newInvoice, poNumber: e.target.value })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        placeholder="e.g., 5918"
                                    />
                                </div>

                                {/* Invoice Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={newInvoice.invoiceDate}
                                        onChange={(e) => setNewInvoice({ ...newInvoice, invoiceDate: e.target.value })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    />
                                </div>

                                {/* Amount */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Amount <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">$</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={newInvoice.amount}
                                            onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                                            className="block w-full pl-7 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>

                                {/* Supplier */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Supplier <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={newInvoice.supplierId}
                                        onChange={(e) => setNewInvoice({ ...newInvoice, supplierId: e.target.value })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    >
                                        <option value="">Select supplier...</option>
                                        {suppliers.map(supplier => (
                                            <option key={supplier.id} value={supplier.id}>
                                                {supplier.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* PDF File */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Invoice PDF</label>
                                    <input
                                        type="file"
                                        accept="application/pdf"
                                        onChange={handleInvoiceFileChange}
                                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                    />
                                    {invoicePdfFile && (
                                        <p className="mt-1 text-sm text-green-600">
                                            Selected: {invoicePdfFile.name}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Footer Buttons */}
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAddInvoiceModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveInvoice}
                                    disabled={savingInvoice}
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {savingInvoice ? 'Saving...' : 'Save Invoice'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Invoice Modal */}
            {editInvoiceModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setEditInvoiceModal(null)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Invoice</h3>

                            {editInvoiceError && (
                                <div className="mb-4 rounded-md bg-red-50 p-3">
                                    <p className="text-sm text-red-700">{editInvoiceError}</p>
                                </div>
                            )}

                            <div className="space-y-4">
                                {/* Invoice Number (read-only) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Invoice #</label>
                                    <input
                                        type="text"
                                        value={editInvoiceModal.invoiceNumber}
                                        disabled
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-100 text-gray-500 sm:text-sm"
                                    />
                                </div>

                                {/* PO Number */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">PO #</label>
                                    <input
                                        type="text"
                                        value={editInvoiceModal.poNumber}
                                        onChange={(e) => setEditInvoiceModal({ ...editInvoiceModal, poNumber: e.target.value })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    />
                                </div>

                                {/* Invoice Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={editInvoiceModal.invoiceDate}
                                        onChange={(e) => setEditInvoiceModal({ ...editInvoiceModal, invoiceDate: e.target.value })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    />
                                </div>

                                {/* Amount */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Amount <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">$</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={editInvoiceModal.amount}
                                            onChange={(e) => setEditInvoiceModal({ ...editInvoiceModal, amount: e.target.value })}
                                            className="block w-full pl-7 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Supplier */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Supplier <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={editInvoiceModal.supplierId}
                                        onChange={(e) => setEditInvoiceModal({ ...editInvoiceModal, supplierId: e.target.value })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    >
                                        <option value="">Select supplier...</option>
                                        {suppliers.map(supplier => (
                                            <option key={supplier.id} value={supplier.id}>
                                                {supplier.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setEditInvoiceModal(null)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveEditInvoice}
                                    disabled={editingInvoice}
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {editingInvoice ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirmModal(null)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 text-center mb-2">Delete Invoice</h3>
                            <p className="text-sm text-gray-500 text-center mb-4">
                                Are you sure you want to delete invoice <span className="font-semibold">{deleteConfirmModal.documentNumber}</span>?
                                {deleteConfirmModal.itemCount > 0 && (
                                    <span className="block mt-1">
                                        This will also delete {deleteConfirmModal.itemCount} line item(s).
                                    </span>
                                )}
                            </p>
                            <p className="text-xs text-red-600 text-center mb-4">This action cannot be undone.</p>

                            <div className="flex justify-center space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setDeleteConfirmModal(null)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDeleteInvoice}
                                    disabled={deletingInvoice}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                                >
                                    {deletingInvoice ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
